import type { RateView, RunDetail, RunEvent } from "@checkone/contract/client";
import type { ReactNode } from "react";
import { FileRef } from "../../components/FileRef";
import { RateCell } from "../../components/RateCell";
import { ActionedCell } from "./Actioned";
import { RatesAppliedTable } from "./RatesApplied";
import {
  SOURCE_LABEL,
  formatDayShort,
  formatDay,
  formatLongDate,
  formatTime,
  pct,
  shaPrefix,
} from "../../lib/format";

const STATUS_WORD = { active: "Active", not_listed: "Not on the list" } as const;
const KIND_PREFIX = {
  weekly: "",
  first_check: "First check · ",
  correction: "Correction · ",
  vendor_check: "Vendor check · ",
} as const;

function wasRate(r: RateView | null): string | null {
  return r && r.kind === "rate" ? `was ${pct(r.rate_pct)}` : null;
}

/**
 * One rendering of a run, shared by /runs, /runs/:id and the signed public page /r/:token, so the
 * message, the app and the proof link always show the same truth.
 */
export function RunView({
  run,
  events,
  actions,
  nav,
  footer,
  tenantName,
  ack,
}: {
  run: RunDetail;
  events: RunEvent[];
  /** Download and export buttons, under the proof (signed-in only). */
  actions?: ReactNode;
  /** Previous / next check links beside the title. */
  nav?: ReactNode;
  /** A line under the changes table (signed-in only: links into the app). */
  footer?: ReactNode;
  tenantName?: string;
  /**
   * The actioned trail (signed-in only): each change shows who marked it actioned and why; `canMark` adds
   * the control (members and owners; viewers only read).
   */
  ack?: { canMark: boolean };
}) {
  const s = run.summary;
  const it = run.files.find((f) => f.source === "ATL_IT");
  const st = run.files.find((f) => f.source === "ATL_ST");
  return (
    <div className="stack-lg">
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h1>
            {KIND_PREFIX[run.kind]}
            {formatLongDate(run.finished_at)}
          </h1>
          <p>
            {tenantName && <>{tenantName} · </>}
            {s.vendors_checked} {s.vendors_checked === 1 ? "vendor" : "vendors"} checked at{" "}
            {formatTime(run.finished_at)} against FBR files of {formatDayShort(it?.published_on)} (income tax)
            and {formatDayShort(st?.published_on)} (sales tax) · {s.changes} changed · {s.not_on_atl} not on
            income-tax ATL
            {run.message?.sent_at && <> · sent {formatTime(run.message.sent_at)}</>}
          </p>
        </div>
        {nav}
      </div>

      {run.status === "stale" && (
        <div className="callout warn">
          <p>FBR has not published a new list since the previous check. These are the last known statuses.</p>
        </div>
      )}
      {run.status === "failed" && (
        <div className="callout danger">
          <p>This check did not complete. Statuses below are from the files it read before it stopped.</p>
        </div>
      )}

      <div className="split">
        <section className="panel" aria-labelledby="changes-h">
          <div className="panel-head">
            <h2 id="changes-h">Changes ({events.length})</h2>
            <span className="subtle">Was → now, and the rate that applies now</span>
          </div>
          {events.length === 0 ? (
            <p className="muted" style={{ padding: "var(--space-4) 1.25rem", margin: 0 }}>
              {run.kind === "vendor_check"
                ? "This check read the new or changed NTN and CNIC of the vendors edited; their statuses are on each vendor's page. Changes are reported by the Monday check."
                : "No vendor changed status in this check."}
            </p>
          ) : (
            <div className="table-wrap responsive">
              <table className="data responsive">
                <caption className="sr-only">Status changes in this run</caption>
                <thead>
                  <tr>
                    <th scope="col">Vendor</th>
                    <th scope="col">List</th>
                    <th scope="col">Was → now</th>
                    <th scope="col">Rate now</th>
                    {ack && <th scope="col">Actioned</th>}
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id}>
                      <td data-label="">
                        <div className="vendor-name">{e.vendor_name}</div>
                        <div className="ident">
                          {e.identifier.type} {e.identifier.display}
                        </div>
                      </td>
                      <td data-label="List" className="text-2">
                        {SOURCE_LABEL[e.source]}
                      </td>
                      <td data-label="Was → now">
                        <span className="nowrap">
                          {STATUS_WORD[e.old_status]} →{" "}
                          <span className={`pill ${e.new_status === "active" ? "ok" : "warn"}`}>
                            {STATUS_WORD[e.new_status]}
                          </span>
                        </span>
                        <div className="subtle">
                          {e.change === "added" ? "Re-entered" : "Dropped off"} · file{" "}
                          {formatDayShort(e.from_file.published_on)} →{" "}
                          {formatDayShort(e.to_file.published_on)} ·{" "}
                          <span className="mono">{shaPrefix(e.to_file.sha256, 8)}</span>
                        </div>
                      </td>
                      <td data-label="Rate now">
                        {e.rate_now ? (
                          <>
                            <RateCell rate={e.rate_now} showPayee />
                            {wasRate(e.rate_before) && <div className="subtle">{wasRate(e.rate_before)}</div>}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      {ack && (
                        <td data-label="Actioned">
                          <ActionedCell event={e} canMark={ack.canMark} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {footer && <div className="panel-foot">{footer}</div>}
        </section>

        <div className="stack">
          <section className="stack" style={{ gap: "var(--space-2)" }} aria-labelledby="msg-h">
            <h2 id="msg-h" className="label" style={{ margin: 0 }}>
              The message
            </h2>
            {run.message ? (
              <div className="message-text">{run.message.text}</div>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                None: a vendor check is recorded here, not sent.
              </p>
            )}
          </section>

          <section className="stack" style={{ gap: "var(--space-2)" }} aria-labelledby="proof-h">
            <h2 id="proof-h" className="label" style={{ margin: 0 }}>
              Proof
            </h2>
            <div className="files">
              {run.files.map((f) => (
                <FileRef key={f.id} file={f} />
              ))}
            </div>
            <div className="file-ref">
              {run.rate_table ? (
                <span>
                  Rate table <span className="mono">v{run.rate_table.version}</span>, effective{" "}
                  {formatDay(run.rate_table.effective_from)}
                </span>
              ) : (
                <span>Rate table pending approval</span>
              )}
            </div>
            <div className="file-ref">
              <span>
                Result hash{" "}
                <span className="mono" title={run.result_sha256}>
                  {shaPrefix(run.result_sha256, 16)}
                </span>
              </span>
            </div>
            <p className="subtle" style={{ margin: 0 }}>
              Replaying this check from the same FBR files reproduces this hash exactly. Rates are FBR's
              published rates for the section; confirm exemptions with your advisor.
            </p>
          </section>

          {actions && (
            <div className="stack" style={{ gap: "var(--space-2)" }}>
              {actions}
            </div>
          )}
        </div>
      </div>

      <RatesAppliedTable applied={run.rates_applied} />
    </div>
  );
}
